import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ShoppingBag, CalendarDays, ClipboardList, LayoutTemplate, FlaskConical, Users, MoreHorizontal } from 'lucide-react'
import { clsx } from 'clsx'
import { BottomSheet } from '@/ui'

const primaryTabs = [
  { name: 'Orders', href: '/orders', icon: ShoppingBag },
  { name: 'Events', href: '/events', icon: CalendarDays },
]

const moreItems = [
  { name: 'Menu', href: '/master-menu', icon: ClipboardList },
  { name: 'Materials', href: '/master-materials', icon: FlaskConical },
  { name: 'Vendors', href: '/vendors', icon: Users },
  { name: 'Templates', href: '/menu-templates', icon: LayoutTemplate },
]

function isTabActive(href: string, pathname: string) {
  const isSetupVendors = /^\/events\/[^/]+\/setup-vendors/.test(pathname)
  const isSetupMenu = /^\/events\/[^/]+\/setup-menu/.test(pathname)
  if (href === '/events') return pathname === '/events' || isSetupVendors
  if (href === '/orders') return pathname === '/orders' || pathname.startsWith('/orders/') || isSetupMenu
  return pathname === href || pathname.startsWith(href + '/')
}

export default function BottomNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const [moreOpen, setMoreOpen] = useState(false)

  const isMoreActive = moreItems.some(item => isTabActive(item.href, location.pathname))

  return (
    <>
      <nav aria-label="Primary" className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--surface-card)] border-t border-[var(--border-default)] safe-area-bottom">
        <div className="flex items-stretch h-14 very-short:h-11">
          {primaryTabs.map((tab) => {
            const isActive = isTabActive(tab.href, location.pathname)
            return (
              <Link
                key={tab.name}
                to={tab.href}
                className={clsx(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors duration-150',
                  isActive
                    ? 'text-[var(--brand-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                )}
              >
                <tab.icon className={clsx(
                  'h-5 w-5 very-short:h-4 very-short:w-4',
                  isActive ? 'text-[var(--brand-primary)]' : 'text-[var(--text-muted)]'
                )} />
                <span className="very-short:hidden">{tab.name}</span>
              </Link>
            )
          })}

          {/* More tab */}
          <button
            onClick={() => setMoreOpen(true)}
            aria-label="More navigation options"
            className={clsx(
              'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors duration-150',
              isMoreActive
                ? 'text-[var(--brand-primary)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            )}
          >
            <MoreHorizontal className={clsx(
              'h-5 w-5 very-short:h-4 very-short:w-4',
              isMoreActive ? 'text-[var(--brand-primary)]' : 'text-[var(--text-muted)]'
            )} />
            <span className="very-short:hidden">More</span>
          </button>
        </div>
      </nav>

      {/* More sheet */}
      <BottomSheet isOpen={moreOpen} onClose={() => setMoreOpen(false)}>
        <div className="px-4 pt-4 pb-6">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-3">Master Data</p>
          <div className="space-y-0.5">
            {moreItems.map(({ name, href, icon: Icon }) => {
              const isActive = isTabActive(href, location.pathname)
              return (
                <button
                  key={name}
                  onClick={() => { setMoreOpen(false); navigate(href) }}
                  className={clsx(
                    'flex w-full items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-[var(--brand-primary-subtle)] text-[var(--brand-primary)]'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]'
                  )}
                >
                  <Icon className={clsx('h-5 w-5', isActive ? 'text-[var(--brand-primary)]' : 'text-[var(--text-muted)]')} />
                  {name}
                </button>
              )
            })}
          </div>
        </div>
      </BottomSheet>
    </>
  )
}
